import { useEffect, useState } from 'react';
import { Alert } from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomTextField,
  CustomTextArea,
} from '../../../common/components';
import {
  useDeliverConsumption,
  usePrintConsumptionDelivery,
} from '../../../hooks/useConsumptions';
import {
  buildDeliverPayload,
  downloadConsumptionDeliveryPdf,
  getConsumptionApiErrorMessage,
  getInitialDeliverState,
  validateDeliverConsumptionForm,
} from '../utils/consumptionsUtils';
import { useReportPendingState } from '../../hooks/useReportPendingState';
import { guardDialogClose } from '../../utils/actionLockUtils';

const DeliverConsumptionDialog = ({
  open,
  onClose,
  consumption,
  onSuccess,
  onPendingChange,
}) => {
  const [formData, setFormData] = useState(getInitialDeliverState);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const deliverMutation = useDeliverConsumption();
  const printMutation = usePrintConsumptionDelivery();
  const isLocked = deliverMutation.isPending || printMutation.isPending;
  useReportPendingState(isLocked, onPendingChange);

  useEffect(() => {
    if (open) {
      setFormData(getInitialDeliverState());
      setErrors({});
      setErrorMessage('');
    }
  }, [open, consumption?.uuid]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateDeliverConsumptionForm(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!consumption?.uuid) return;

    try {
      setErrorMessage('');
      const payload = buildDeliverPayload(formData);
      const updatedConsumption = await deliverMutation.mutateAsync({
        id: consumption.uuid,
        payload,
      });

      let pdfGenerated = false;
      try {
        if (updatedConsumption.pdf_base64) {
          downloadConsumptionDeliveryPdf(updatedConsumption);
          pdfGenerated = true;
        } else {
          await printMutation.mutateAsync(updatedConsumption.uuid);
          pdfGenerated = true;
        }
      } catch (printError) {
        onSuccess?.(updatedConsumption, {
          pdfGenerated: false,
          printError,
        });
        onClose();
        return;
      }

      onSuccess?.(updatedConsumption, { pdfGenerated });
      onClose();
    } catch (error) {
      setErrorMessage(
        getConsumptionApiErrorMessage(error, 'No se pudo registrar la entrega')
      );
    }
  };

  if (!open) return null;

  return (
    <DialogContainer
      open={open}
      onClose={guardDialogClose(isLocked, onClose)}
      size="medium"
    >
      <DialogTitle>Registrar entrega</DialogTitle>

      <DialogBody sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        <CustomTextField
          label="Consumo"
          name="consumptionNumber"
          value={consumption?.consumption_number || '-'}
          disabled
        />

        <CustomTextField
          label="Responsable"
          name="delivery_responsible"
          value={formData.delivery_responsible}
          onChange={handleChange}
          required
          disabled={isLocked}
          error={Boolean(errors.delivery_responsible)}
          helperText={errors.delivery_responsible}
        />

        <CustomTextField
          label="Dirección de entrega"
          name="delivery_address"
          value={formData.delivery_address}
          onChange={handleChange}
          required
          disabled={isLocked}
          error={Boolean(errors.delivery_address)}
          helperText={errors.delivery_address}
        />

        <CustomTextField
          label="Fecha de entrega"
          name="delivery_date"
          type="date"
          value={formData.delivery_date}
          onChange={handleChange}
          required
          disabled={isLocked}
          error={Boolean(errors.delivery_date)}
          helperText={errors.delivery_date}
          InputLabelProps={{ shrink: true }}
        />

        <CustomTextField
          label="Hora de entrega"
          name="delivery_time"
          type="time"
          value={formData.delivery_time}
          onChange={handleChange}
          required
          disabled={isLocked}
          error={Boolean(errors.delivery_time)}
          helperText={errors.delivery_time}
          InputLabelProps={{ shrink: true }}
        />

        <CustomTextArea
          label="Observaciones"
          name="observations"
          value={formData.observations}
          onChange={handleChange}
          disabled={isLocked}
        />
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSubmit}
        saveLabel="Registrar entrega"
        saveButtonProps={{ isLoading: isLocked }}
        cancelButtonProps={{ disabled: isLocked }}
      />
    </DialogContainer>
  );
};

export default DeliverConsumptionDialog;
